import * as React from 'react';

interface RoastCountdownProps {
  roastLevel: string;
  createdAt: string;
  onClose: () => void;
  onLoadingAnyway?: () => void;
}

const RoastCountdown: React.FC<RoastCountdownProps> = ({ 
  roastLevel, 
  createdAt, 
  onClose,
  onLoadingAnyway 
}) => {
  const [timeLeft, setTimeLeft] = React.useState<{ hours: number; minutes: number; seconds: number }>({ 
    hours: 0, 
    minutes: 0, 
    seconds: 0 
  });
  const [isVisible, setIsVisible] = React.useState(true);

  React.useEffect(() => {
    // Calculate time remaining until 24 hours after creation
    const calculateTimeLeft = () => {
      const creationTime = new Date(createdAt).getTime();
      const expiryTime = creationTime + (24 * 60 * 60 * 1000); // 24 hours in milliseconds
      const now = new Date().getTime();
      const difference = expiryTime - now;
      
      if (difference > 0) {
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        
        setTimeLeft({ hours, minutes, seconds });
      } else {
        // Time has expired
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
        clearInterval(timer);
      }
    };
    
    calculateTimeLeft(); // Calculate immediately
    const timer = setInterval(calculateTimeLeft, 1000); // Update every second
    
    return () => clearInterval(timer);
  }, [createdAt]);

  // Get roast level emoji
  const getRoastLevelEmoji = (level: string) => {
    switch (level.toLowerCase()) {
      case 'mild': return '❄️';
      case 'medium': return '🔥';
      case 'spicy': return '🌶️';
      case 'nuclear': return '☢️';
      default: return '🔥';
    }
  };

  // Get funny message based on roast level
  const getFunnyMessage = (level: string) => {
    switch (level.toLowerCase()) {
      case 'mild':
        return "This product is still recovering from its gentle roasting. It needs some time to chill before it can handle another critique.";
      case 'medium':
        return "This product is currently applying aloe vera to its burn wounds. Please check back later for another roasting session.";
      case 'spicy':
        return "Whoa there! This product is still feeling the heat from its last roasting. The fire department has asked us to wait before setting it ablaze again.";
      case 'nuclear':
        return "DANGER! This product is still radioactive from its nuclear roasting. Hazmat teams are working around the clock to make it safe for another round.";
      default:
        return "This product needs some time to recover before it can be roasted again. Check back later!";
    }
  };

  // Get background color based on roast level
  const getBackgroundColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'mild': return 'rgba(96, 165, 250, 0.95)'; // Light blue
      case 'medium': return 'rgba(249, 115, 22, 0.95)'; // Orange
      case 'spicy': return 'rgba(239, 68, 68, 0.95)'; // Red
      case 'nuclear': return 'rgba(124, 45, 18, 0.95)'; // Dark brown
      default: return 'rgba(249, 115, 22, 0.95)';
    }
  };

  if (!isVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: getBackgroundColor(roastLevel),
        borderRadius: '12px',
        padding: '30px',
        maxWidth: '500px',
        width: '100%',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
        color: 'white',
        position: 'relative',
        animation: 'popIn 0.5s ease-out forwards'
      }}>
        <button 
          onClick={() => {
            setIsVisible(false);
            onClose();
          }}
          style={{
            position: 'absolute',
            top: '15px',
            right: '15px',
            background: 'transparent',
            border: 'none',
            color: 'white',
            fontSize: '24px',
            cursor: 'pointer',
            padding: '0',
            lineHeight: '1'
          }}
        >
          ×
        </button>
        
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <span style={{ fontSize: '48px', display: 'block', marginBottom: '10px' }}>
            {getRoastLevelEmoji(roastLevel)}
          </span>
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: 'bold', 
            marginBottom: '10px',
            textTransform: 'uppercase'
          }}>
            {roastLevel.toUpperCase()} ROAST COOLDOWN
          </h2>
          <p style={{ fontSize: '16px', marginBottom: '25px' }}>
            {getFunnyMessage(roastLevel)}
          </p>
        </div>
        
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: '15px', 
          marginBottom: '25px'
        }}>
          <div style={{ 
            textAlign: 'center', 
            backgroundColor: 'rgba(255, 255, 255, 0.2)', 
            borderRadius: '8px', 
            padding: '10px 15px',
            minWidth: '80px'
          }}>
            <div style={{ fontSize: '28px', fontWeight: 'bold' }}>{timeLeft.hours}</div>
            <div style={{ fontSize: '14px' }}>HOURS</div>
          </div>
          <div style={{ 
            textAlign: 'center', 
            backgroundColor: 'rgba(255, 255, 255, 0.2)', 
            borderRadius: '8px', 
            padding: '10px 15px',
            minWidth: '80px'
          }}>
            <div style={{ fontSize: '28px', fontWeight: 'bold' }}>{timeLeft.minutes}</div>
            <div style={{ fontSize: '14px' }}>MINUTES</div>
          </div>
          <div style={{ 
            textAlign: 'center', 
            backgroundColor: 'rgba(255, 255, 255, 0.2)', 
            borderRadius: '8px', 
            padding: '10px 15px',
            minWidth: '80px'
          }}>
            <div style={{ fontSize: '28px', fontWeight: 'bold' }}>{timeLeft.seconds}</div>
            <div style={{ fontSize: '14px' }}>SECONDS</div>
          </div>
        </div>
        
        <button 
          onClick={() => {
            setIsVisible(false);
            if (onLoadingAnyway) {
              onLoadingAnyway();
            }
          }}
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            color: '#333',
            border: 'none',
            borderRadius: '6px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            width: '100%',
            transition: 'background-color 0.2s',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 1)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
          }}
        >
          Load Anyway
        </button>
      </div>
      
      <style>{`
        @keyframes popIn {
          0% { transform: scale(0.8); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default RoastCountdown;
